import React from "react";
import { SectionHeading } from "@/components/Bits";
import {
  ExternalLink, Linkedin, Quote, Lightbulb, Sparkles, Cpu, Rocket, HandshakeIcon,
  Building2, Compass, Zap, Users, TrendingUp, Landmark, GraduationCap, Home as HomeIcon,
} from "lucide-react";

const APPROACH = [
  { icon: Lightbulb, title: "Innovation" },
  { icon: Sparkles, title: "Entrepreneurship" },
  { icon: Cpu, title: "Technology" },
  { icon: Zap, title: "Strong Execution" },
  { icon: Users, title: "Customer Trust" },
  { icon: Building2, title: "Professional Management" },
  { icon: TrendingUp, title: "Long-Term Growth" },
];

const JOURNEY = [
  { icon: Compass, label: "Vision" },
  { icon: Rocket, label: "Strategy" },
  { icon: Users, label: "People" },
  { icon: Zap, label: "Execution" },
  { icon: TrendingUp, label: "Scale" },
];

const VENTURES = [
  {
    icon: HomeIcon,
    name: "Bhumi Group & Developers",
    role: "Owner",
    desc: "A real-estate-focused business involved in property development, land projects, sales, marketing and customer-focused real estate solutions. Palash's experience here spans project planning, sales, marketing, customer relationships and business operations.",
  },
  {
    icon: Landmark,
    name: "Swarnbhumi Infratech",
    role: "Owner",
    desc: "Associated with infrastructure and real-estate-related business activities.",
  },
  {
    icon: GraduationCap,
    name: "Udayanam Private Limited",
    role: "Founder",
    desc: "An organisation focused on education, career development, counselling, assessment and youth development — helping individuals understand their abilities and make better educational and career decisions.",
  },
];

const FOUNDER_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/job_daily-nutrition-hub-6/artifacts/v3c1f2i7_ChatGPT%20Image%20Jul%206%2C%202026%2C%2004_57_51%20PM.png";

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 text-center">
          <div className="eyebrow">About ASURE OPC Private Limited</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight text-asure-text">
            Building Businesses.<br/>
            <span className="text-asure-orange italic">Creating Value.</span> Shaping the Future.
          </h1>
          <p className="mt-8 text-lg text-asure-text2 leading-relaxed max-w-3xl mx-auto">
            ASURE OPC Private Limited is an entrepreneur-led organisation founded by <span className="text-asure-forest font-semibold">Palash Pandey</span>, with a vision to build innovative, scalable and professionally managed businesses across emerging sectors.
          </p>
        </div>
      </section>

      {/* INTRO PARAGRAPHS */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-8 space-y-5 text-asure-text2 text-base md:text-lg leading-relaxed">
        <p>ASURE OPC Private Limited represents the next phase of Palash Pandey's entrepreneurial journey, bringing together his experience in real estate, education, business development, marketing, entrepreneurship and brand building.</p>
        <p>The company is focused on identifying emerging opportunities, developing differentiated business models, building strong teams and creating brands that can generate long-term value for customers, partners and society.</p>
        <p>ASURE's long-term vision is to develop scalable businesses, strong consumer brands and professionally managed organisations that can grow from India to wider markets.</p>
      </section>

      {/* VISION */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="rounded-3xl p-10 md:p-16 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, var(--asure-forest), var(--asure-deep) 55%, #0a3a24)" }}
          data-testid="vision-block">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(rgba(212,175,55,0.6) 1px, transparent 1px)", backgroundSize: "5px 5px" }} />
          <div className="relative grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-start">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
              <Compass style={{ color: "var(--asure-gold)" }} />
            </div>
            <div>
              <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>Our Vision</div>
              <blockquote className="font-display text-3xl md:text-4xl mt-3 leading-tight italic">
                "To build a globally trusted nutrition and wellness brand that inspires people to live better, healthier, and more fulfilling lives — every day."
              </blockquote>
              <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-3xl">
                We envision ASURE becoming a leading Indian-origin wellness brand with a global presence, creating high-quality, innovative and accessible nutrition products for modern lifestyles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-16 md:pb-20" data-testid="mission-block">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          <div>
            <div className="eyebrow">Our Mission</div>
            <blockquote className="font-display text-3xl md:text-4xl mt-3 leading-tight italic text-asure-text">
              "To make quality nutrition simple, accessible, trustworthy, and a meaningful part of everyday life."
            </blockquote>
            <p className="mt-6 text-asure-text2 text-sm leading-relaxed">
              Seven commitments that guide how ASURE builds its nutrition and wellness portfolio.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              "Develop thoughtfully formulated nutrition and wellness products.",
              "Select quality ingredients with care and responsibility.",
              "Make everyday nutrition simple and convenient.",
              "Build products that fit naturally into modern lifestyles.",
              "Educate consumers through transparent and responsible communication.",
              "Continuously innovate and expand our wellness product portfolio.",
              "Build a trusted brand from India for customers across the world.",
            ].map((m, i) => (
              <li key={m} className="card-surface p-4 flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: "var(--asure-forest)" }}>{i + 1}</span>
                <span className="text-sm text-asure-text2 leading-relaxed">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-16 md:py-20" style={{ background: "var(--asure-ivory)" }} data-testid="promise-block">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="eyebrow">Our Promise</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight text-asure-text">
            Better Nutrition.<br/>
            Better Choices.<br/>
            <span className="text-asure-orange italic">Better Every Day.</span>
          </h2>
          <div className="mt-8 inline-flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full border border-asure-forest/30 text-asure-forest">
            ASURE — Better Every Day.
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-16 md:pb-20">
        <SectionHeading eyebrow="Our Approach" title="How ASURE builds." subtitle="Seven principles that guide every business we build under the ASURE umbrella." />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {APPROACH.map(({ icon: I, title }, i) => (
            <div key={title} data-testid={`approach-${title.toLowerCase().replace(/\s+/g, '-')}`}
              className="card-surface p-6 hover:-translate-y-1 transition-transform duration-500"
              style={{ transitionDelay: `${i * 30}ms` }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--asure-ivory)", color: "var(--asure-forest)" }}><I size={18} /></div>
              <div className="font-display text-xl mt-4">{title}</div>
            </div>
          ))}
          <div className="card-surface p-6 col-span-2 md:col-span-3 lg:col-span-1 flex items-center" style={{ background: "var(--asure-forest)", color: "white" }}>
            <p className="text-sm leading-relaxed">
              Create businesses that solve real problems, deliver meaningful customer value and have the potential to scale significantly.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-16" data-testid="founder-section">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="sticky top-28">
              <div className="rounded-3xl overflow-hidden relative shadow-xl">
                <img src={FOUNDER_IMG} alt="Palash Pandey, Founder — Asure OPC Private Limited" className="w-full aspect-[4/5] object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white"
                  style={{ background: "linear-gradient(180deg, rgba(15,81,50,0) 0%, rgba(15,81,50,0.85) 55%, rgba(10,26,18,0.95) 100%)" }}>
                  <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>Founder</div>
                  <div className="font-display text-3xl md:text-4xl mt-2 leading-tight">Palash Pandey</div>
                  <div className="mt-2 text-xs md:text-sm text-white/85 leading-relaxed">
                    Entrepreneur · Business Leader · Founder
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a href="https://asurelife.com" target="_blank" rel="noopener noreferrer" data-testid="founder-asure-link"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-white/15 border border-white/25 hover:bg-white/25 transition-colors backdrop-blur">
                      <ExternalLink size={11} /> asurelife.com
                    </a>
                    <a href="https://www.linkedin.com/in/palash-pandey-295005180/" target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-white/15 border border-white/25 hover:bg-white/25 transition-colors backdrop-blur">
                      <Linkedin size={11} /> LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow">Meet the Founder</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              An entrepreneur building for the long term.
            </h2>

            <div className="mt-8 space-y-5 text-asure-text2 leading-relaxed">
              <p>Palash Pandey is an entrepreneur and business leader with experience across real estate, education, business development, marketing, youth development and entrepreneurship.</p>
              <p>He is the Founder of <span className="text-asure-forest font-semibold">ASURE OPC Private Limited</span> and is focused on developing ASURE into a scalable and diversified business organisation.</p>
              <p>His entrepreneurial journey includes building businesses, developing teams, creating brands and exploring opportunities across multiple sectors.</p>
              <p>Palash believes in identifying opportunities, building strong teams, executing ideas effectively and creating businesses with long-term potential.</p>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER VENTURES */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <SectionHeading eyebrow="Other Ventures" title="A portfolio built over years." subtitle="Businesses and initiatives Palash Pandey has built and led across real estate, infrastructure and education." />
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {VENTURES.map(({ icon: I, name, role, desc }) => (
            <div key={name} className="card-surface p-8 h-full flex flex-col" data-testid={`venture-${name.toLowerCase().split(' ')[0]}`}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--asure-ivory)", color: "var(--asure-forest)" }}><I size={18} /></div>
              <div className="mt-6">
                <div className="eyebrow" style={{ color: "var(--asure-orange)" }}>{role}</div>
                <div className="font-display text-2xl mt-2 leading-tight">{name}</div>
                <p className="mt-3 text-sm text-asure-text2 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* JOURNEY (Vision → Strategy → People → Execution → Scale) */}
      <section className="py-16 md:py-24" style={{ background: "var(--asure-ivory)" }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow="The Entrepreneurial Journey" title="From idea to scale."
            subtitle="Palash's journey spans real estate, education, marketing and business development. With ASURE, the vision is to move beyond individual ventures and build scalable businesses and strong brands with the potential to create long-term economic and social value." />
          <div className="mt-14 flex flex-wrap md:flex-nowrap items-stretch gap-3 md:gap-2" data-testid="journey">
            {JOURNEY.map(({ icon: I, label }, i) => (
              <React.Fragment key={label}>
                <div className="flex-1 min-w-[140px] card-surface p-6 text-center bg-white">
                  <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center" style={{ background: "var(--asure-forest)", color: "white" }}>
                    <I size={16} />
                  </div>
                  <div className="mt-4 eyebrow" style={{ color: "var(--asure-forest)" }}>Step {i + 1}</div>
                  <div className="font-display text-2xl mt-1">{label}</div>
                </div>
                {i < JOURNEY.length - 1 && (
                  <div className="hidden md:flex items-center text-asure-forest text-2xl font-bold">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP PHILOSOPHY QUOTE */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
        <div className="eyebrow">Leadership Philosophy</div>
        <div className="mt-6 relative rounded-3xl p-10 md:p-16 border border-asure-forest/15 text-left"
          style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.10), rgba(15,81,50,0.05))" }}>
          <Quote className="absolute -top-5 left-8 w-10 h-10 rounded-full p-2 text-white" style={{ background: "var(--asure-gold)" }} />
          <p className="font-display text-3xl md:text-4xl leading-snug text-asure-text">
            "Build with vision. Grow with purpose. Create lasting value."
          </p>
          <div className="mt-6 text-sm text-asure-text2 leading-relaxed">
            Palash Pandey believes that a successful business should not only generate revenue, but also solve problems, create opportunities, build trust and create something that lasts.
          </div>
          <div className="mt-3 text-sm text-asure-text2 leading-relaxed">
            Through ASURE OPC Private Limited, his objective is to build businesses that combine <span className="text-asure-forest font-semibold">entrepreneurial thinking, innovation and professional execution.</span>
          </div>
          <div className="mt-6 text-xs eyebrow">— Palash Pandey, Founder</div>
        </div>
      </section>

      {/* Company card */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-24">
        <div className="card-surface p-10 text-center">
          <div className="eyebrow">The Company</div>
          <h3 className="font-display text-3xl mt-3">ASURE OPC PRIVATE LIMITED</h3>
          <p className="mt-3 text-asure-text2">Asure Building, Sewai, Nahar Road<br/>Sushant Golf City, Lucknow, Uttar Pradesh, India</p>
        </div>
      </section>
    </div>
  );
}
